import { FormEvent, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { DocumentationPageTreeNode } from '../../services/documentationApi';

type DocumentationMovePageModalProps = {
  open: boolean;
  pageId: string;
  pageTitle: string;
  tree: DocumentationPageTreeNode[];
  onClose: () => void;
  onConfirm: (parentPageId: string | null) => void;
};

function flattenExcluding(
  nodes: DocumentationPageTreeNode[],
  excludeIds: Set<string>,
  depth = 0,
): { id: string; title: string; depth: number }[] {
  const result: { id: string; title: string; depth: number }[] = [];
  for (const node of nodes) {
    if (excludeIds.has(node.id)) continue;
    result.push({ id: node.id, title: node.title, depth });
    if (node.children?.length) {
      result.push(...flattenExcluding(node.children, excludeIds, depth + 1));
    }
  }
  return result;
}

function collectDescendantIds(node: DocumentationPageTreeNode): string[] {
  const ids = [node.id];
  for (const child of node.children ?? []) {
    ids.push(...collectDescendantIds(child));
  }
  return ids;
}

function findNode(nodes: DocumentationPageTreeNode[], id: string): DocumentationPageTreeNode | null {
  for (const node of nodes) {
    if (node.id === id) return node;
    const found = findNode(node.children ?? [], id);
    if (found) return found;
  }
  return null;
}

export function DocumentationMovePageModal({
  open,
  pageId,
  pageTitle,
  tree,
  onClose,
  onConfirm,
}: DocumentationMovePageModalProps) {
  const [parentId, setParentId] = useState('');

  const options = useMemo(() => {
    const node = findNode(tree, pageId);
    const exclude = new Set(node ? collectDescendantIds(node) : [pageId]);
    return flattenExcluding(tree, exclude);
  }, [tree, pageId]);

  if (!open) return null;

  function handleSubmit(event: FormEvent): void {
    event.preventDefault();
    onConfirm(parentId || null);
  }

  return createPortal(
    <div className="docs-modal-backdrop" role="presentation" onClick={onClose}>
      <div
        className="docs-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="docs-move-title"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 id="docs-move-title" className="docs-modal__title">
          Mover “{pageTitle}”
        </h3>
        <form className="space-y-4" onSubmit={handleSubmit}>
          <label className="erp-label-block">
            Nova página pai
            <select className="erp-input" value={parentId} onChange={(e) => setParentId(e.target.value)}>
              <option value="">— Raiz —</option>
              {options.map((opt) => (
                <option key={opt.id} value={opt.id}>
                  {'—'.repeat(opt.depth)} {opt.title}
                </option>
              ))}
            </select>
          </label>
          <div className="flex flex-wrap gap-2 justify-end">
            <button type="button" className="erp-btn-secondary" onClick={onClose}>
              Cancelar
            </button>
            <button type="submit" className="erp-btn-primary">
              Mover
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body,
  );
}
